import { supabase } from './supabaseClient';
import { describeError } from './errorMessage';
import { readOnlyMessage } from './readOnlyNotice';
import {
    aktuelleKatalogVersion,
    einzufrierendeZeitraeume,
    ergaenzeHistorie,
    gewerkeAusPlan,
    leereEinrichtung,
    leereOrdnung,
    mitPlanfassung,
} from '../components/handbuch/handbuchLogic';
import type { ZeitraumEinfrierung } from '../components/handbuch/handbuchLogic';
import type {
    Baustelleneinrichtung,
    Baustellenordnung,
    GewerkAusPlan,
    Handbuch,
    HandbuchGewerk,
    HandbuchLage,
    HandbuchNachweis,
    HandbuchStatus,
    HistorieEintrag,
    NeuerHandbuchNachweis,
    NeuesHandbuchGewerk,
    PlanSchritt,
} from '../components/handbuch/types';

/**
 * Datenbankzugriff des Moduls „SiGe-Handbuch" (Tabellen `handbuecher`,
 * `handbuch_gewerke`, `handbuch_nachweise` — supabase/handbuch_koordinierung.sql,
 * MIGRATIONS.md Nr. 40). Die einzige Stelle, die mit ihnen redet.
 *
 * Die REGELN liegen daneben in `src/components/handbuch/handbuchLogic.ts` und
 * sind ohne Datenbank pruefbar; hier steht nur, was ohne Supabase nicht zu
 * haben ist.
 *
 * ═══ VIER HAUSREGELN ═══════════════════════════════════════════════════════
 *
 * (a) LESEN IST FAIL-OPEN, SCHREIBEN NICHT. Fehlt die Tabelle (Migration Nr. 40
 *     noch nicht gefahren), liefert das Lesen eine leere Lage statt zu werfen —
 *     dann gibt es eben noch kein Handbuch, und die Kachel oeffnet sich
 *     trotzdem. Jeder schreibende Aufruf meldet den Fehler dagegen sichtbar.
 *     Muster: planungsphaseService.ts und projektDokumenteService.ts.
 *
 * (b) ZWEITE REIHE DES SCHREIBSCHUTZES. Jeder schreibende Aufruf verlangt einen
 *     `SchreibKontext` und prueft ihn selbst. Das ist kein Guertel-und-
 *     Hosentraeger: dieses Modul haengt an EIGENEN Tabellen, hinter denen kein
 *     `handleUpdateReport`-Wachposten steht, und `isReadOnly` kann zur Laufzeit
 *     kippen, wenn die Projektsperre an einen anderen Benutzer geht. Weil der
 *     Kontext ein Pflichtparameter ist, kann ein neuer Schreibweg die Pruefung
 *     nicht vergessen — der Compiler laesst ihn sonst nicht uebersetzen.
 *
 * (c) KEIN LESEN-DANN-SCHREIBEN. Wo eine Bedingung gilt (nur Entwuerfe aendern,
 *     nur einmal freigeben), steht sie als `where`-Bedingung IM UPDATE und
 *     nicht als vorheriges `select`. Zwischen Lesen und Schreiben passt eine
 *     zweite Sitzung.
 *
 * (d) KEIN AUTOMATISCHES ANLEGEN. `fetchHandbuchLage` legt nichts an. Ein
 *     Projekt ohne Handbuch bleibt ohne Handbuch, bis jemand `ensureHandbuch`
 *     ausloest. Dieselbe Ueberlegung wie beim Schreibschutz fuer
 *     Nur-Ansicht-Projekte, wo Selbstheilungs-Migrationen beim reinen Lesen
 *     geschrieben hatten.
 */

const T_HANDBUCH = 'handbuecher';
const T_GEWERK = 'handbuch_gewerke';
const T_NACHWEIS = 'handbuch_nachweise';

/** PostgREST liefert je nach Schema-Cache 42P01 oder PGRST205. */
const errorCode = (e: unknown): string => {
    const c = (e as { code?: unknown })?.code;
    return typeof c === 'string' ? c : '';
};
const istFehlendeTabelle = (e: unknown): boolean => {
    const code = errorCode(e);
    return code === 'PGRST205' || code === '42P01';
};
const istDublette = (e: unknown): boolean => errorCode(e) === '23505';

// ═══════════════════════════════════════════════════════════════════════════
// SCHREIBSCHUTZ — die zweite Reihe
// ═══════════════════════════════════════════════════════════════════════════

export interface SchreibKontext {
    /** Nur-Ansicht: aus „Offene Positionen" geoeffnet oder fremde Projektsperre. */
    nurAnsicht: boolean;
    /**
     * Status des Handbuchs, wie ihn der Container beim Klick kannte.
     *
     * `freigegeben` sperrt die Kapitel (Entscheidung Nr. 8). Fuer
     * `handbuecher` steht dieselbe Bedingung zusaetzlich als `where` im UPDATE;
     * `handbuch_gewerke` und `handbuch_nachweise` tragen keine Statusspalte —
     * dort ist diese Pruefung die einzige, und sie muss deshalb wirklich an
     * jedem Aufruf haengen.
     */
    status?: HandbuchStatus;
}

const GESPERRT_TEXT = 'Das Handbuch ist freigegeben und eingefroren. '
    + 'Für Änderungen bitte eine neue Fassung eröffnen — die freigegebene Fassung bleibt erhalten.';

/** Wirft, wenn im Lesemodus geschrieben werden soll. */
const pruefeSchreibrecht = (ctx: SchreibKontext, was: string): void => {
    if (!ctx || ctx.nurAnsicht) throw new Error(readOnlyMessage(was));
};

/** Wie oben, zusaetzlich gegen die Freigabe. */
const pruefeAenderbar = (ctx: SchreibKontext, was: string): void => {
    pruefeSchreibrecht(ctx, was);
    if (ctx.status === 'freigegeben') throw new Error(GESPERRT_TEXT);
};

// ═══════════════════════════════════════════════════════════════════════════
// ABBILDUNG — Vorgaben fuer das, was die Datenbank NULL-bar haelt
// ═══════════════════════════════════════════════════════════════════════════
//
// Ohne diese Vorgaben steht in einem Textfeld irgendwann „null", und eine
// `.map()` auf einer jsonb-Spalte, die als Objekt statt als Liste zurueckkommt,
// stuerzt die ganze Seite ab. Beides ist im Repo schon passiert.

const alsObjekt = <T>(wert: unknown, vorgabe: T): T =>
    (wert && typeof wert === 'object' && !Array.isArray(wert)) ? (wert as T) : vorgabe;

const alsListe = <T>(wert: unknown): T[] => (Array.isArray(wert) ? (wert as T[]) : []);

const alsText = (wert: unknown): string => (typeof wert === 'string' ? wert : '');

/**
 * Die Baustellenordnung einer Zeile, mit aufgefuellten Luecken.
 *
 * `handbuecher.ordnung` hat den Vorgabewert `{}`. Eine Zeile, die nicht ueber
 * `ensureHandbuch` entstanden ist (Wiederherstellung, Migration), traegt
 * deshalb gar keine Schluessel, und die Kapitelseite liefe auf
 * `undefined.length`.
 *
 * Aufgefuellt wird mit LEEREN Werten und niemals mit den Vorschlagstexten:
 * was in der Spalte steht, gilt — was fehlt, fehlt. Sonst stuenden Regeln auf
 * dem Aushang, die niemand beschlossen hat.
 */
const alsOrdnung = (wert: unknown): Baustellenordnung => {
    const roh = alsObjekt<Partial<Baustellenordnung>>(wert, {});
    return {
        arbeitszeiten: alsText(roh.arbeitszeiten),
        zugang: alsText(roh.zugang),
        regeln: alsListe<string>(roh.regeln),
        notfallschritte: alsListe<string>(roh.notfallschritte),
        sammelplatz: alsText(roh.sammelplatz),
    };
};

/** Wie `alsOrdnung`, aus demselben Grund. */
const alsEinrichtung = (wert: unknown): Baustelleneinrichtung => {
    const roh = alsObjekt<Partial<Baustelleneinrichtung>>(wert, {});
    return {
        versorgung: alsText(roh.versorgung),
        entsorgung: alsText(roh.entsorgung),
        lagerplatz: alsText(roh.lagerplatz),
        verkehrsfuehrung: alsText(roh.verkehrsfuehrung),
        bemerkung: alsText(roh.bemerkung),
    };
};

const mapHandbuch = (row: Record<string, unknown>): Handbuch => ({
    ...(row as unknown as Handbuch),
    ordnung: alsOrdnung(row.ordnung),
    einrichtung: alsEinrichtung(row.einrichtung),
    historie: alsListe<HistorieEintrag>(row.historie),
    stand: alsText(row.stand),
    freigegeben_von_name: alsText(row.freigegeben_von_name),
    katalog_version: alsText(row.katalog_version),
});

const mapGewerk = (row: Record<string, unknown>): HandbuchGewerk => ({
    ...(row as unknown as HandbuchGewerk),
    gewerk_norm: alsText(row.gewerk_norm),
    gewerk_label: alsText(row.gewerk_label),
    anhang_ii: alsListe<string>(row.anhang_ii),
    psa: alsListe(row.psa),
    unterweisungen: alsListe(row.unterweisungen),
    notfall: alsObjekt(row.notfall, { ersthelfer: '', besonderheiten: '' }),
    witterung: alsObjekt(row.witterung, { eintraege: [] }),
    rab32: alsObjekt(row.rab32, { eintraege: [] }),
    ordnung: alsObjekt(row.ordnung, { regeln: [] }),
    sortierung: typeof row.sortierung === 'number' ? row.sortierung : 0,
});

const mapNachweis = (row: Record<string, unknown>): HandbuchNachweis => ({
    ...(row as unknown as HandbuchNachweis),
    nr: alsText(row.nr),
    bezeichnung: alsText(row.bezeichnung),
    bemerkung: alsText(row.bemerkung),
    sortierung: typeof row.sortierung === 'number' ? row.sortierung : 0,
});

/**
 * Spalten, die der Client NIE mitschickt.
 *
 * `updated_at` kommt vom Trigger, die Identitaeten und Fremdschluessel stehen
 * fest — ein Update, das sie mitschickt, kann eine Zeile in ein fremdes
 * Projekt umhaengen. `katalog_version` steht bewusst mit drin: sie haelt fest,
 * mit WELCHEM Katalog beurteilt wurde, und darf sich nicht nachtraeglich
 * aendern, nur weil der Katalog inzwischen gewachsen ist.
 */
const GESPERRTE_SPALTEN = new Set([
    'id', 'project_id', 'handbuch_id', 'handbuch_gewerk_id',
    'created_at', 'updated_at', 'katalog_version',
]);

const ohneGesperrteSpalten = <T extends object>(updates: T): Record<string, unknown> => {
    const rein: Record<string, unknown> = {};
    Object.entries(updates as Record<string, unknown>).forEach(([k, v]) => {
        if (GESPERRTE_SPALTEN.has(k)) return;
        // Leerstring bei Datumsspalten heisst „keine Angabe" und muss als NULL
        // ankommen — PostgREST wuerde '' sonst als Datum parsen wollen.
        if (v === '' && (k.startsWith('zeitraum_') || k.startsWith('gueltig_') || k === 'frist')) {
            rein[k] = null;
        } else {
            rein[k] = v;
        }
    });
    return rein;
};

/** PostgREST deckelt bei 1000 Zeilen OHNE MELDUNG. */
const SEITE = 1000;

// ═══════════════════════════════════════════════════════════════════════════
// KOPFSATZ
// ═══════════════════════════════════════════════════════════════════════════

/** Das Handbuch eines Projekts, oder `null`, wenn es noch keins gibt.
 *  Wirft NICHT bei fehlender Tabelle (siehe (a) im Dateikopf). */
export const fetchHandbuch = async (projectId: string): Promise<Handbuch | null> => {
    if (!projectId || projectId === 'temp-id') return null;
    const { data, error } = await supabase
        .from(T_HANDBUCH)
        .select('*')
        .eq('project_id', projectId)
        .maybeSingle();
    if (error) {
        if (istFehlendeTabelle(error)) return null;
        throw new Error(describeError(error));
    }
    return data ? mapHandbuch(data) : null;
};

/**
 * Legt das Handbuch an — oder gibt das bestehende zurueck.
 *
 * Wird NUR auf ausdrueckliche Handlung aufgerufen, nie beim Oeffnen der Kachel
 * (siehe (d)). Der Unique-Index auf `project_id` ist der Riegel: zwei
 * gleichzeitig geoeffnete Sitzungen wuerden sonst zwei Koepfe anlegen. Bei
 * `23505` wird deshalb nicht gemeldet, sondern der vorhandene Satz gelesen —
 * das ist kein Fehler, sondern das erwartete Ergebnis des Wettlaufs.
 *
 * `katalog_version` MUSS mitgeschrieben werden: die Spalte hat bewusst keinen
 * Vorgabewert, damit spaeter nachvollziehbar bleibt, mit welchem Katalog
 * beurteilt wurde.
 *
 * `ordnung` kommt LEER auf die Welt. Frueher schrieb dieser Insert die sieben
 * Regeln und sechs Notfallschritte der Vorbelegung mit — und damit galt das
 * Pflichtkapitel „Baustellenordnung" ab der ersten Sekunde als vollstaendig,
 * die Freigabe war frei, und die Saetze hingen als beschlossene Regeln am
 * Bauzaun. Der Vorschlag wird jetzt in `Baustellenordnung.tsx` angeboten und
 * von einem Menschen uebernommen (siehe `leereOrdnung`).
 */
export const ensureHandbuch = async (
    ctx: SchreibKontext,
    projectId: string,
): Promise<Handbuch> => {
    pruefeSchreibrecht(ctx, 'ein SiGe-Handbuch anzulegen');
    if (!projectId || projectId === 'temp-id') {
        throw new Error('Ohne gespeichertes Projekt kann kein Handbuch angelegt werden.');
    }

    const vorhanden = await fetchHandbuch(projectId);
    if (vorhanden) return vorhanden;

    const { data, error } = await supabase
        .from(T_HANDBUCH)
        .insert({
            project_id: projectId,
            status: 'entwurf',
            katalog_version: aktuelleKatalogVersion(),
            ordnung: leereOrdnung(),
            einrichtung: leereEinrichtung(),
            historie: [],
        })
        .select('*')
        .single();

    if (error) {
        if (istDublette(error)) {
            const jetzt = await fetchHandbuch(projectId);
            if (jetzt) return jetzt;
        }
        throw new Error(describeError(error));
    }
    return mapHandbuch(data);
};

/**
 * Aendert ein Kapitel des Kopfsatzes.
 *
 * Der Riegel steht ZWEIMAL: `pruefeAenderbar` im Client und
 * `.eq('status','entwurf')` im UPDATE. Der zweite ist der wirksame — zwischen
 * dem Laden der Seite und dem Klick kann jemand anderes freigegeben haben.
 * Trifft das UPDATE keine Zeile, wird das gemeldet und nicht verschwiegen:
 * sonst tippt jemand weiter in ein eingefrorenes Handbuch.
 */
export const updateHandbuch = async (
    ctx: SchreibKontext,
    id: string,
    updates: Partial<Handbuch>,
): Promise<Handbuch> => {
    pruefeAenderbar(ctx, 'das Handbuch zu ändern');
    const rein = ohneGesperrteSpalten(updates);
    if (!Object.keys(rein).length) throw new Error('Es gibt nichts zu speichern.');

    const { data, error } = await supabase
        .from(T_HANDBUCH)
        .update(rein)
        .eq('id', id)
        .eq('status', 'entwurf')
        .select('*');
    if (error) throw new Error(describeError(error));

    const zeile = (data || [])[0];
    if (!zeile) throw new Error(GESPERRT_TEXT);
    return mapHandbuch(zeile);
};

/**
 * Schreibt EINEN festgehaltenen Zeitraum in eine Gewerkezeile.
 *
 * Die `.is(..., null)`-Bedingungen sind kein Zierrat, sondern der Grund, warum
 * dieser Schritt gefahrlos wiederholbar ist: geschrieben wird ausschliesslich
 * in ein Ende, das in der Datenbank noch leer ist. Ein von Hand gesetzter oder
 * bei einer frueheren Freigabe festgehaltener Zeitraum kann dadurch niemals
 * ueberschrieben werden — auch nicht von einem zweiten Freigabeversuch, der
 * gleichzeitig laeuft.
 */
const friereZeitraumEin = async (z: ZeitraumEinfrierung): Promise<void> => {
    const rein: Record<string, string> = {};
    if (z.zeitraum_von) rein.zeitraum_von = z.zeitraum_von;
    if (z.zeitraum_bis) rein.zeitraum_bis = z.zeitraum_bis;
    if (!Object.keys(rein).length) return;

    let frage = supabase.from(T_GEWERK).update(rein).eq('id', z.id);
    if (rein.zeitraum_von) frage = frage.is('zeitraum_von', null);
    if (rein.zeitraum_bis) frage = frage.is('zeitraum_bis', null);

    const { error } = await frage;
    if (error) throw new Error(describeError(error));
};

/**
 * Friert das Handbuch ein.
 *
 * ═══ EINGEFROREN HEISST: OHNE RUECKGRIFF AUF DEN LAUFENDEN PLAN ════════════
 * Der Statuswechsel allein genuegte NICHT. Eine Gewerkeseite mit
 * `zeitraum_von = NULL` rechnet ihren Zeitraum bei jedem Druck aus dem
 * aktuellen SiGe-Plan (`wirksamerZeitraum`). Damit zeigte derselbe Druck
 * derselben freigegebenen Fassung nach einer Planverschiebung andere Daten —
 * ohne Historieneintrag und ohne Zutun. Deshalb werden die plangespeisten
 * Werte hier in die Zeilen geschrieben, und die beurteilte Planfassung wird im
 * Historieneintrag vermerkt (`mitPlanfassung`).
 *
 * ═══ REIHENFOLGE: ERST EINFRIEREN, DANN STEMPELN ═══════════════════════════
 * Die eine Zusage, die halten muss, lautet: es gibt keine freigegebene
 * Fassung, die noch live liest. Scheitert das Einfrieren, wird nicht
 * freigegeben und der Fehler steht sichtbar da — ein erneuter Versuch ist
 * gefahrlos, weil `friereZeitraumEin` nur leere Enden fuellt. Umgekehrt (erst
 * stempeln) koennte die Freigabe stehen und das Einfrieren fehlen, und das
 * merkte niemand.
 *
 * Der Guard steht IM UPDATE (siehe (c)): ein bereits freigegebenes Handbuch
 * wird nicht ein zweites Mal gestempelt, sonst verschoebe ein versehentlicher
 * Doppelklick das Freigabedatum — und das Datum ist im Streitfall der Beleg.
 *
 * Der Historieneintrag geht MIT demselben UPDATE hinaus. Getrennt geschrieben
 * koennte die Freigabe stehen und der Vermerk fehlen; dann waere im Handbuch
 * eine Fassung freigegeben, die in der Historie nicht vorkommt.
 *
 * Gibt `null` zurueck, wenn bereits freigegeben war.
 */
export const freigebenHandbuch = async (
    ctx: SchreibKontext,
    handbuch: Handbuch,
    eintrag: HistorieEintrag,
    jetztIso: string,
): Promise<Handbuch | null> => {
    pruefeAenderbar(ctx, 'das Handbuch freizugeben');

    // Der Planstand wird HIER geholt und nicht vom Aufrufer erwartet: sonst
    // haenge die Beweiskraft der Freigabe daran, ob vorher jemand den
    // Gewerke-Reiter geoeffnet hat.
    const [stand, gewerke] = await Promise.all([
        ladePlanStand(handbuch.project_id),
        fetchHandbuchGewerke(handbuch.id),
    ]);
    // Nebenlaeufig und nicht der Reihe nach: die Zeilen sind voneinander
    // unabhaengig, und ein Abbruch mittendrin ist ungefaehrlich — der naechste
    // Versuch fuellt nur, was noch leer ist.
    await Promise.all(einzufrierendeZeitraeume(gewerke, stand.ausPlan).map(friereZeitraumEin));

    const { data, error } = await supabase
        .from(T_HANDBUCH)
        .update({
            status: 'freigegeben' as HandbuchStatus,
            freigegeben_am: jetztIso,
            freigegeben_von_name: eintrag.autor || '',
            version_label: handbuch.version_label || eintrag.nr,
            historie: ergaenzeHistorie(
                handbuch.historie || [],
                mitPlanfassung(eintrag, stand.sigeProjektId),
            ),
        })
        .eq('id', handbuch.id)
        .eq('status', 'entwurf')
        .select('*');
    if (error) throw new Error(describeError(error));

    const zeile = (data || [])[0];
    return zeile ? mapHandbuch(zeile) : null;
};

/**
 * Eroeffnet eine neue Fassung eines freigegebenen Handbuchs.
 *
 * Entscheidung Nr. 8, zweiter Halbsatz: die spaetere Aenderung ERZEUGT einen
 * neuen Eintrag in `historie` und ueberschreibt die freigegebene Fassung
 * nicht. Genau deshalb wird hier der Vermerk angehaengt, BEVOR der Status
 * zurueck auf `entwurf` geht — sonst gaebe es einen Zeitraum, in dem niemand
 * mehr sehen kann, dass jemals freigegeben war.
 *
 * `freigegeben_am` und `freigegeben_von_name` bleiben absichtlich STEHEN: sie
 * gehoeren zur letzten Freigabe, die es tatsaechlich gab.
 */
export const neueFassungHandbuch = async (
    ctx: SchreibKontext,
    handbuch: Handbuch,
    eintrag: HistorieEintrag,
): Promise<Handbuch> => {
    pruefeSchreibrecht(ctx, 'eine neue Fassung des Handbuchs zu eröffnen');

    const { data, error } = await supabase
        .from(T_HANDBUCH)
        .update({
            status: 'entwurf' as HandbuchStatus,
            version_label: eintrag.nr,
            historie: ergaenzeHistorie(handbuch.historie || [], eintrag),
        })
        .eq('id', handbuch.id)
        .eq('status', 'freigegeben')
        .select('*');
    if (error) throw new Error(describeError(error));

    const zeile = (data || [])[0];
    if (!zeile) throw new Error('Das Handbuch ist bereits in Bearbeitung.');
    return mapHandbuch(zeile);
};

// ═══════════════════════════════════════════════════════════════════════════
// GEWERKESEITEN
// ═══════════════════════════════════════════════════════════════════════════

export const fetchHandbuchGewerke = async (handbuchId: string): Promise<HandbuchGewerk[]> => {
    if (!handbuchId) return [];
    const alle: HandbuchGewerk[] = [];
    for (let von = 0; ; von += SEITE) {
        const { data, error } = await supabase
            .from(T_GEWERK)
            .select('*')
            .eq('handbuch_id', handbuchId)
            .order('sortierung', { ascending: true })
            // Zweites Kriterium: `sortierung` allein ist nicht eindeutig, und
            // eine uneindeutige Sortierung laesst beim Blaettern Zeilen doppelt
            // erscheinen oder ganz ausfallen.
            .order('id', { ascending: true })
            .range(von, von + SEITE - 1);
        if (error) {
            if (istFehlendeTabelle(error)) return [];
            throw new Error(describeError(error));
        }
        const seite = data || [];
        alle.push(...seite.map(mapGewerk));
        if (seite.length < SEITE) return alle;
    }
};

/**
 * Legt Gewerkeseiten an.
 *
 * `upsert` mit `ignoreDuplicates` statt `insert`: der Unique-Index
 * (handbuch_id, gewerk_norm) bleibt der Riegel, aber ein zweiter Klick auf
 * „aus dem Plan nachziehen" soll nicht mit 23505 abbrechen, sondern schlicht
 * nichts zusaetzlich anlegen. Zwei gleichzeitig offene Sitzungen legten sonst
 * beide dieselbe Seite an.
 */
export const createHandbuchGewerke = async (
    ctx: SchreibKontext,
    handbuchId: string,
    projectId: string,
    entwuerfe: NeuesHandbuchGewerk[],
): Promise<HandbuchGewerk[]> => {
    pruefeAenderbar(ctx, 'Gewerkeseiten anzulegen');
    if (!handbuchId || !projectId || !entwuerfe?.length) return [];

    const { data, error } = await supabase
        .from(T_GEWERK)
        .upsert(
            entwuerfe.map(e => ({ ...e, handbuch_id: handbuchId, project_id: projectId })),
            { onConflict: 'handbuch_id,gewerk_norm', ignoreDuplicates: true },
        )
        .select('*');
    if (error) throw new Error(describeError(error));
    return (data || []).map(mapGewerk);
};

export const updateHandbuchGewerk = async (
    ctx: SchreibKontext,
    id: string,
    updates: Partial<HandbuchGewerk>,
): Promise<HandbuchGewerk> => {
    pruefeAenderbar(ctx, 'eine Gewerkeseite zu ändern');
    const rein = ohneGesperrteSpalten(updates);
    if (!Object.keys(rein).length) throw new Error('Es gibt nichts zu speichern.');

    const { data, error } = await supabase
        .from(T_GEWERK)
        .update(rein)
        .eq('id', id)
        .select('*')
        .single();
    if (error) throw new Error(describeError(error));
    return mapGewerk(data);
};

/**
 * Loescht eine Gewerkeseite.
 *
 * Die Nachweise gehen ueber `on delete cascade` mit. Das ist Absicht und der
 * Grund, warum die Oberflaeche vorher fragt: eine Gewerkeseite zu loeschen
 * heisst, die dafuer eingesammelten Nachweise zu verlieren. Wer ein Gewerk nur
 * aus dem PLAN genommen hat, loescht hier NICHTS — solche Seiten werden als
 * verwaist markiert (siehe `abgleicheGewerke`).
 */
export const deleteHandbuchGewerk = async (ctx: SchreibKontext, id: string): Promise<void> => {
    pruefeAenderbar(ctx, 'eine Gewerkeseite zu löschen');
    const { error } = await supabase.from(T_GEWERK).delete().eq('id', id);
    if (error) throw new Error(describeError(error));
};

// ═══════════════════════════════════════════════════════════════════════════
// NACHWEISE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Alle Nachweise eines Projekts.
 *
 * Ueber `project_id` und nicht ueber die Gewerk-Ids: eine `in`-Liste mit 24
 * Ids waere laenger als die Abfrage selbst, und die Spalte traegt einen
 * eigenen Index. Denselben Weg geht `fetchProjektDokumente`.
 */
export const fetchHandbuchNachweise = async (projectId: string): Promise<HandbuchNachweis[]> => {
    if (!projectId || projectId === 'temp-id') return [];
    const alle: HandbuchNachweis[] = [];
    for (let von = 0; ; von += SEITE) {
        const { data, error } = await supabase
            .from(T_NACHWEIS)
            .select('*')
            .eq('project_id', projectId)
            .order('sortierung', { ascending: true })
            .order('id', { ascending: true })
            .range(von, von + SEITE - 1);
        if (error) {
            if (istFehlendeTabelle(error)) return [];
            throw new Error(describeError(error));
        }
        const seite = data || [];
        alle.push(...seite.map(mapNachweis));
        if (seite.length < SEITE) return alle;
    }
};

export const createHandbuchNachweise = async (
    ctx: SchreibKontext,
    projectId: string,
    gewerkId: string,
    entwuerfe: NeuerHandbuchNachweis[],
): Promise<HandbuchNachweis[]> => {
    pruefeAenderbar(ctx, 'Nachweise anzulegen');
    if (!projectId || !gewerkId || !entwuerfe?.length) return [];

    const { data, error } = await supabase
        .from(T_NACHWEIS)
        .insert(entwuerfe.map(e => ({ ...e, project_id: projectId, handbuch_gewerk_id: gewerkId })))
        .select('*');
    if (error) throw new Error(describeError(error));
    return (data || []).map(mapNachweis);
};

export const updateHandbuchNachweis = async (
    ctx: SchreibKontext,
    id: string,
    updates: Partial<HandbuchNachweis>,
): Promise<HandbuchNachweis> => {
    pruefeAenderbar(ctx, 'einen Nachweis zu ändern');
    const rein = ohneGesperrteSpalten(updates);
    if (!Object.keys(rein).length) throw new Error('Es gibt nichts zu speichern.');

    const { data, error } = await supabase
        .from(T_NACHWEIS)
        .update(rein)
        .eq('id', id)
        .select('*')
        .single();
    if (error) throw new Error(describeError(error));
    return mapNachweis(data);
};

export const deleteHandbuchNachweis = async (ctx: SchreibKontext, id: string): Promise<void> => {
    pruefeAenderbar(ctx, 'einen Nachweis zu löschen');
    const { error } = await supabase.from(T_NACHWEIS).delete().eq('id', id);
    if (error) throw new Error(describeError(error));
};

/**
 * Haengt Nachweise an eine andere Gewerkeseite — der Schreibteil des
 * Zusammenfuehrens.
 *
 * Ein einziges UPDATE mit `in(...)` und nicht eine Schleife: bricht die
 * Verbindung mitten in einer Schleife ab, haengen die Nachweise halb hier und
 * halb dort, und beim naechsten Oeffnen fehlt niemandem etwas — weil die
 * Zeilen ja da sind, nur unter der falschen Seite. Was WELCHE Zeilen umziehen,
 * entscheidet `fuehreGewerkeZusammen` (pure, getestet); hier wird nur
 * geschrieben.
 *
 * Gibt die Zahl der tatsaechlich umgehaengten Zeilen zurueck — der Aufrufer
 * kann sie gegen die erwartete Zahl halten.
 */
export const haengeNachweiseUm = async (
    ctx: SchreibKontext,
    nachweisIds: string[],
    zielGewerkId: string,
): Promise<number> => {
    pruefeAenderbar(ctx, 'Nachweise einer anderen Gewerkeseite zuzuordnen');
    if (!nachweisIds?.length || !zielGewerkId) return 0;

    const { data, error } = await supabase
        .from(T_NACHWEIS)
        .update({ handbuch_gewerk_id: zielGewerkId })
        .in('id', nachweisIds)
        .select('id');
    if (error) throw new Error(describeError(error));
    return (data || []).length;
};

// ═══════════════════════════════════════════════════════════════════════════
// GESAMTLAGE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Alles auf einmal. Legt NICHTS an (siehe (d) im Dateikopf).
 *
 * Ohne Kopfsatz wird gar nicht weitergefragt: Gewerkeseiten und Nachweise
 * haengen am Handbuch, und zwei Abfragen, die sicher leer zurueckkommen,
 * kosten auf schmaler Leitung genau so viel wie zwei, die etwas liefern.
 */
export const fetchHandbuchLage = async (projectId: string): Promise<HandbuchLage> => {
    const handbuch = await fetchHandbuch(projectId);
    if (!handbuch) return { handbuch: null, gewerke: [], nachweise: [] };
    const [gewerke, nachweise] = await Promise.all([
        fetchHandbuchGewerke(handbuch.id),
        fetchHandbuchNachweise(projectId),
    ]);
    return { handbuch, gewerke, nachweise };
};

// ═══════════════════════════════════════════════════════════════════════════
// PLANFASSUNGEN — nur lesend
// ═══════════════════════════════════════════════════════════════════════════

/** Eine Planfassung, so weit das Kapitel „Änderungshistorie" sie braucht. */
export interface Planfassung {
    id: string;
    plan_nr?: string;
    version_label?: string | null;
    created_at?: string;
    status?: string;
}

/**
 * Die Planfassungen eines Projekts fuer das Kapitel „Aenderungshistorie".
 *
 * NUR LESEN. Auf `sige_*` schreibt dieses Modul nichts — Auflage aus Abschnitt
 * 10.3 des Plans, und es gibt fuer diese Tabellen kein SQL im Repo, an dem sich
 * ein Schreibweg pruefen liesse.
 *
 * `.is('deleted_at', null)` ist tragend: `sige_projekte` loescht WEICH
 * (`versioning.ts`, `deleted_at` steht in keinem TS-Interface). Ohne den Filter
 * listete die Historie verworfene Fassungen als gueltige Staende — genau die
 * Aussage, die dieses Blatt widerlegen soll.
 *
 * Fail-open wie alles Lesende hier: fehlt die Tabelle oder greift eine Regel,
 * bleibt die Liste leer und das Handbuch trotzdem druckbar.
 */
export const fetchPlanfassungen = async (projectId: string): Promise<Planfassung[]> => {
    if (!projectId || projectId === 'temp-id') return [];
    const { data, error } = await supabase
        .from('sige_projekte')
        .select('id, plan_nr, version_label, created_at, status')
        .eq('project_id', projectId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(SEITE);
    if (error) {
        if (istFehlendeTabelle(error)) return [];
        throw new Error(describeError(error));
    }
    return (data || []) as Planfassung[];
};

/** Der Planstand, aus dem eine Freigabe ihre Zeitraeume festhaelt. */
export interface PlanStand {
    /** `sige_projekte.id` der beurteilten Fassung, oder `null` ohne Plan. */
    sigeProjektId: string | null;
    /** Die Gewerke dieser Fassung, nach `gewerk_norm` — dieselbe Form, die die
     *  Kapitel und `wirksamerZeitraum` erwarten. */
    ausPlan: Map<string, GewerkAusPlan>;
}

/**
 * Liest die Gewerke der neuesten, nicht geloeschten Planfassung.
 *
 * Die FASSUNG kommt aus `fetchPlanfassungen` und nicht aus einer zweiten
 * Abfrage: dort steht der `.is('deleted_at', null)`-Filter bereits, und zwei
 * Abfragen mit unterschiedlichen Filtern lieferten frueher oder spaeter zwei
 * verschiedene „neueste" Fassungen. Nur die Arbeitsschritte kommen dazu —
 * Gefaehrdungen, Massnahmen und Bloecke braucht das Einfrieren nicht.
 *
 * Fail-open wie alles Lesende hier: ohne Plan bleibt der Stand leer, und die
 * Freigabe friert dann eben nichts ein statt zu scheitern. Ein Handbuch ohne
 * SiGe-Plan ist ein gueltiger Zustand.
 */
export const ladePlanStand = async (projectId: string): Promise<PlanStand> => {
    const leer: PlanStand = { sigeProjektId: null, ausPlan: new Map() };
    if (!projectId || projectId === 'temp-id') return leer;

    const neueste = (await fetchPlanfassungen(projectId))[0];
    if (!neueste?.id) return leer;

    const { data, error } = await supabase
        .from('sige_arbeitsschritte')
        .select('id, gewerk, zeitraum_von, zeitraum_bis')
        .eq('sige_projekt_id', neueste.id)
        .order('sortierung');
    if (error) {
        if (istFehlendeTabelle(error)) return { sigeProjektId: neueste.id, ausPlan: new Map() };
        throw new Error(describeError(error));
    }

    const gewerke = gewerkeAusPlan((data || []) as PlanSchritt[]);
    return {
        sigeProjektId: neueste.id,
        ausPlan: new Map(gewerke.map(g => [g.gewerk_norm, g])),
    };
};
